// components/CastingCallDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Divider,
  Avatar,
  Tooltip,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Public as WebIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const platformIcons = {
  instagram: <InstagramIcon fontSize="small" />,
  youtube: <YouTubeIcon fontSize="small" />,
  facebook: <FacebookIcon fontSize="small" />,
  twitter: <TwitterIcon fontSize="small" />,
  linkedin: <LinkedInIcon fontSize="small" />,
  tiktok: <WorkIcon fontSize="small" />,
  other: <WebIcon fontSize="small" />,
};

export default function CastingCallDialog({ open, onClose, castingCall, posterInfo }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [copying, setCopying] = useState(false);

  if (!castingCall) return null;

  const {
    id,
    brief,
    budget,
    call_type,
    platforms = [],
    work_types = [],
    applicant_count = 0,
    public_slug,
  } = castingCall;

  const publicUrl = `${window.location.origin}/casting-calls/public/${public_slug}`;
  const isCreatorCall = call_type === 'creator';

  const handleCopyLink = async () => {
    setCopying(true);
    try {
      await navigator.clipboard.writeText(publicUrl);
      setTimeout(() => setCopying(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopying(false);
    }
  };

  const handleViewFullPage = () => {
    navigate(`/casting-calls/public/${public_slug}`);
    onClose();
  };

  const handleApply = () => {
    navigate(`/casting-calls/public/${public_slug}`);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        }
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
          p: 3,
          pb: 2,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Poster info */}
        {posterInfo && (
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar
              src={posterInfo.pfphref}
              alt={posterInfo.display_name}
              sx={{ width: 48, height: 48 }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {posterInfo.display_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Posted a casting call
              </Typography>
            </Box>
          </Box>
        )}

        {/* Call type badge */}
        <Chip
          icon={<WorkIcon fontSize="small" />}
          label={isCreatorCall ? 'Creator Opportunity' : 'General Work'}
          size="small"
          sx={{
            mb: 1,
            bgcolor: alpha(theme.palette.secondary.main, 0.15),
            color: theme.palette.secondary.main,
            fontWeight: 600,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
          }}
        />

        {/* Brief/Title */}
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {brief}
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Key stats */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
            mb: 3,
          }}
        >
          {/* Budget card */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <MoneyIcon sx={{ color: 'success.main', fontSize: 20 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                BUDGET
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={700} color="success.main">
              ${budget?.toLocaleString() || 0}
            </Typography>
          </Box>

          {/* Applicants card */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <PeopleIcon sx={{ color: 'info.main', fontSize: 20 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                APPLICANTS
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={700} color="info.main">
              {applicant_count}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Platforms (for creator calls) */}
        {isCreatorCall && platforms && platforms.length > 0 && (
          <Box mb={3}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              gutterBottom
              sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}
            >
              Platforms Needed
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {platforms.map((platform, index) => (
                <Chip
                  key={index}
                  icon={platformIcons[platform.toLowerCase()] || platformIcons.other}
                  label={platform}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Work types (for general calls) */}
        {!isCreatorCall && work_types && work_types.length > 0 && (
          <Box mb={3}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              gutterBottom
              sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}
            >
              Work Types
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {work_types.map((type, index) => (
                <Chip
                  key={index}
                  label={type}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Share section */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Share this opportunity
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.secondary',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
              }}
            >
              {publicUrl}
            </Typography>
            <Tooltip title={copying ? 'Copied!' : 'Copy link'}>
              <IconButton
                size="small"
                onClick={handleCopyLink}
                sx={{
                  color: copying ? 'success.main' : 'primary.main',
                }}
              >
                {copying ? <CopyIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogContent>

      {/* Action buttons */}
      <DialogActions
        sx={{
          p: 3,
          pt: 0,
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={handleViewFullPage}
          sx={{
            flex: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          View Full Details
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            flex: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: 'secondary.main',
            '&:hover': {
              bgcolor: 'secondary.dark',
            },
          }}
        >
          Apply Now
        </Button>
      </DialogActions>
    </Dialog>
  );
}